import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Layout/Header';
import Sidebar from '../Layout/Sidebar';

const RoutesPage = () => {
  const navigate = useNavigate();
  const [routeStats, setRouteStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'origin',
    direction: 'ascending',
  });
  const [dataRangeType, setDataRangeType] = useState('historical');

  const fetchRouteStats = useCallback(async (route, token) => {
    try {
      const url = new URL('/api/flights/stats', window.location.origin);
      url.searchParams.append('origin', route.origin);
      url.searchParams.append('destination', route.destination);

      const today = new Date();
      let startDate, endDate;

      if (dataRangeType === 'historical') {
        startDate = new Date();
        startDate.setMonth(today.getMonth() - 6);
        endDate = today;
      } else {
        startDate = today;
        endDate = new Date();
        endDate.setMonth(today.getMonth() + 6);
      }

      url.searchParams.append('startDate', startDate.toISOString().split('T')[0]);
      url.searchParams.append('endDate', endDate.toISOString().split('T')[0]);
      url.searchParams.append('groupBy', 'depart_date');

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) return null;

      const data = await response.json();

      let minPoints = Infinity;
      let maxPoints = 0;
      let totalPoints = 0;
      let totalFlights = 0;

      data.forEach((item) => {
        minPoints = Math.min(minPoints, item.min_points);
        maxPoints = Math.max(maxPoints, item.max_points);
        totalPoints += item.avg_points * item.flight_count;
        totalFlights += item.flight_count;
      });

      const avgPoints =
        totalFlights > 0 ? Math.round(totalPoints / totalFlights) : 0;

      return {
        ...route,
        minPoints: minPoints === Infinity ? 0 : minPoints,
        avgPoints,
        maxPoints,
        flightCount: totalFlights,
      };
    } catch (err) {
      console.error(
        `Stats fetch error for ${route.origin}-${route.destination}:`,
        err
      );
      return {
        ...route,
        minPoints: 0,
        avgPoints: 0,
        maxPoints: 0,
        flightCount: 0,
        error: true,
      };
    }
  }, [dataRangeType]); // Dependency for fetchRouteStats

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetch('/api/auth/verify', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.authenticated) {
          localStorage.removeItem('authToken');
          navigate('/login');
        }
      })
      .catch((err) => {
        console.error('Auth verification error:', err);
        navigate('/login');
      });

    const fetchRoutes = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch('/api/flights/routes', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch routes');

        const data = await response.json();

        const statsPromises = data.map((route) => fetchRouteStats(route, token));
        const statsResults = await Promise.all(statsPromises);

        setRouteStats(statsResults.filter(Boolean));
      } catch (err) {
        setError('Failed to load routes. Please try again.');
        console.error('Route fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [navigate, dataRangeType, fetchRouteStats]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortedAndFilteredRoutes = () => {
    const filtered = routeStats.filter((route) => {
      const search = searchTerm.toLowerCase();
      return (
        route.origin.toLowerCase().includes(search) ||
        route.destination.toLowerCase().includes(search)
      );
    });

    return filtered.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return (
      <span className="ml-1">
        {sortConfig.direction === 'ascending' ? '?' : '?'}
      </span>
    );
  };

  const handleRouteClick = (route) => {
    navigate(
      `/dashboard?origin=${route.origin}&destination=${route.destination}`
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const sortedAndFilteredRoutes = getSortedAndFilteredRoutes();

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onLogout={handleLogout} />
        <main className="flex-1 overflow-y-auto p-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Available Routes
              </h1>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div className="md:w-1/3">
                  <label htmlFor="search" className="sr-only">
                    Search Routes
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <input
                      id="search"
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Search routes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <span className="mr-3 text-sm font-medium text-gray-700">
                    {dataRangeType === 'historical'
                      ? 'Historical Data'
                      : 'Future Data'}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={dataRangeType === 'future'}
                      onChange={() =>
                        setDataRangeType(
                          dataRangeType === 'historical' ? 'future' : 'historical'
                        )
                      }
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:bg-blue-600"></div>
                    <span className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></span>
                  </label>
                </div>
                <div className="text-sm text-gray-500">
                  Showing {sortedAndFilteredRoutes.length} of {routeStats.length}{' '}
                  routes
                </div>
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
            </div>
            {loading ? (
              <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
                <div className="text-gray-500 flex items-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-3 text-blue-500"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading route data...
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort('origin')}
                        >
                          <div className="flex items-center">
                            Origin
                            {getSortDirectionIndicator('origin')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort('destination')}
                        >
                          <div className="flex items-center">
                            Destination
                            {getSortDirectionIndicator('destination')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort('minPoints')}
                        >
                          <div className="flex items-center">
                            Lowest Points
                            {getSortDirectionIndicator('minPoints')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort('avgPoints')}
                        >
                          <div className="flex items-center">
                            Average Points
                            {getSortDirectionIndicator('avgPoints')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort('maxPoints')}
                        >
                          <div className="flex items-center">
                            Highest Points
                            {getSortDirectionIndicator('maxPoints')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort('flightCount')}
                        >
                          <div className="flex items-center">
                            Flights
                            {getSortDirectionIndicator('flightCount')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedAndFilteredRoutes.map((route, index) => (
                        <tr
                          key={`${route.origin}-${route.destination}`}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {route.origin}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {route.destination}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                            {route.minPoints
                              ? route.minPoints.toLocaleString()
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                            {route.avgPoints
                              ? route.avgPoints.toLocaleString()
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                            {route.maxPoints
                              ? route.maxPoints.toLocaleString()
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {route.flightCount
                              ? route.flightCount.toLocaleString()
                              : '0'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <button
                              onClick={() => handleRouteClick(route)}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                      {sortedAndFilteredRoutes.length === 0 && (
                        <tr>
                          <td
                            colSpan="7"
                            className="px-6 py-12 text-center text-gray-500"
                          >
                            No routes found matching your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default RoutesPage;